import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { adminApi } from '../services/api';
import '../assets/TutorDetail.css';

const TutorDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tutor, setTutor] = useState(null);
  const [tutees, setTutees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingSection, setEditingSection] = useState(null);
  const [formData, setFormData] = useState({});
  const [educationList, setEducationList] = useState([]);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [editedName, setEditedName] = useState('');

  useEffect(() => {
    fetchTutorDetails();
    fetchTutorTutees();
  }, [id]);

  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return '';
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const fetchTutorDetails = async () => {
    try {
      setLoading(true);
      const response = await adminApi.getTutorById(id);
      setTutor(response.data);
      setFormData(response.data);
      setEducationList(response.data.educationHistory || []);
      setError('');
    } catch (err) {
      setError('Failed to load tutor details');
    } finally {
      setLoading(false);
    }
  };

  const fetchTutorTutees = async () => {
    try {
      const response = await adminApi.getTutorTutees(id);
      setTutees(response.data);
    } catch (err) {
      console.error('Failed to load tutor tutees:', err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Validation based on field type
    let validatedValue = value;
    
    if (name === 'age') {
      // Age: numbers only
      validatedValue = value.replace(/[^0-9]/g, '');
    } else if (name === 'phone' || name === 'contact.phone') {
      // Phone: numbers, +, spaces, and hyphens only
      validatedValue = value.replace(/[^0-9+\s\-]/g, '');
    } else if (name === 'race' || name === 'country') {
      // Race and Country: letters, spaces, and common punctuation only
      validatedValue = value.replace(/[^a-zA-Z\s\-']/g, '');
    }
    
    if (name.includes('.')) {
      const parts = name.split('.');
      setFormData(prev => {
        const updated = { ...prev };
        let current = updated;
        for (let i = 0; i < parts.length - 1; i++) {
          if (!current[parts[i]]) {
            current[parts[i]] = {};
          }
          current[parts[i]] = { ...current[parts[i]] };
          current = current[parts[i]];
        }
        current[parts[parts.length - 1]] = validatedValue;
        return updated;
      });
    } else {
      setFormData(prev => ({ ...prev, [name]: validatedValue }));
    }
  };

  const startEditing = (section) => {
    setEditingSection(section);
    setFormData(tutor);
    if (section === 'education') {
      setEducationList(tutor.educationHistory || []);
    }
  };

  const cancelEditing = () => {
    setEditingSection(null);
    setFormData(tutor);
    setEducationList(tutor.educationHistory || []);
  };

  // Validation state
  const [fieldErrors, setFieldErrors] = useState({});

  // Validation functions
  const validatePhone = (phone) => {
    if (!phone || phone.trim() === '') return null;
    const phoneRegex = /^[\d\s\-\(\)\+]+$/;
    return phoneRegex.test(phone) ? null : 'Phone can only contain digits, spaces, hyphens, parentheses, and +';
  };

  const validateEmail = (email) => {
    if (!email || email.trim() === '') return null;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email) ? null : 'Email must contain @ and a valid domain';
  };

  const validateYear = (year) => {
    if (!year) return null;
    const currentYear = new Date().getFullYear();
    const yearNum = parseInt(year);
    if (isNaN(yearNum)) return 'Year must be a number';
    if (yearNum < 1900 || yearNum > currentYear + 20) {
      return `Year must be between 1900 and ${currentYear + 20}`;
    }
    return null;
  };

  const validateDateOfBirth = (dob) => {
    if (!dob) return null;
    const date = new Date(dob);
    const now = new Date();
    const minDate = new Date('1900-01-01');
    
    if (isNaN(date.getTime())) return 'Invalid date format';
    if (date < minDate) return 'Date of birth must be after January 1, 1900';
    if (date > now) return 'Date of birth cannot be in the future';
    
    return null;
  };

  const validateAge = (age) => {
    if (!age && age !== 0) return null;
    const ageNum = parseInt(age);
    if (isNaN(ageNum)) return 'Age must be a number';
    if (ageNum < 0 || ageNum > 150) return 'Age must be between 0 and 150';
    return null;
  };

  const validateSection = (sectionName) => {
    const errors = {};
    
    if (sectionName === 'Personal Information') {
      // Validate email (required for tutors)
      const emailError = validateEmail(formData.email);
      if (emailError) errors.email = emailError;
      
      // Validate phone
      if (formData.contact?.phone) {
        const phoneError = validatePhone(formData.contact.phone);
        if (phoneError) errors.phone = phoneError;
      }
      
      // Validate date of birth
      if (formData.dateOfBirth) {
        const dobError = validateDateOfBirth(formData.dateOfBirth);
        if (dobError) errors.dateOfBirth = dobError;
      }
      
      // Validate age
      if (formData.age) {
        const ageError = validateAge(formData.age);
        if (ageError) errors.age = ageError;
      }
    }
    
    if (sectionName === 'Education History') {
      // Validate graduation years
      educationList.forEach((edu, index) => {
        if (edu.yearGraduated) {
          const yearError = validateYear(edu.yearGraduated);
          if (yearError) {
            errors[`yearGraduated${index}`] = yearError;
          }
        }
      });
    }
    
    return errors;
  };

  const showFieldError = (fieldName, errorMessage) => {
    setFieldErrors(prev => ({ ...prev, [fieldName]: errorMessage }));
    
    // Clear error after 5 seconds
    setTimeout(() => {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }, 5000);
  };

  const saveSection = async (sectionName) => {
    try {
      let updateData = { ...formData };
      if (sectionName === 'Education History') {
        updateData.educationHistory = educationList;
      }
      
      console.log('TutorDetail - Saving section:', sectionName);
      console.log('TutorDetail - Update data:', updateData);
      
      // Validate before saving
      const errors = validateSection(sectionName);
      
      if (Object.keys(errors).length > 0) {
        // Show all errors
        setFieldErrors(errors);
        setError('Please fix the validation errors before saving');
        setTimeout(() => setError(''), 3000);
        
        // Clear errors after 5 seconds
        setTimeout(() => setFieldErrors({}), 5000);
        return;
      }
      
      const response = await adminApi.updateTutor(id, updateData);
      setTutor(response.data);
      setFormData(response.data);
      setEducationList(response.data.educationHistory || []);
      setEditingSection(null);
      setFieldErrors({});
      setSuccess(`${sectionName} updated successfully!`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('TutorDetail - Save error:', err);
      setError(`Failed to update ${sectionName.toLowerCase()}: ` + (err.response?.data?.message || err.message));
      setTimeout(() => setError(''), 3000);
    }
  };

  // Education management functions
  const addEducation = () => {
    setEducationList([...educationList, {
      level: '',
      institution: '',
      major: '',
      yearGraduated: '',
      subjects: '',
      grades: ''
    }]);
  };

  const removeEducation = (index) => {
    setEducationList(educationList.filter((_, i) => i !== index));
  };

  const updateEducation = (index, field, value) => {
    const updated = [...educationList];
    updated[index] = { ...updated[index], [field]: value };
    setEducationList(updated);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('image', file);
    try {
      setUploadingImage(true);
      const response = await adminApi.uploadTutorProfileImage(id, formData);
      setTutor(prev => ({ ...prev, profileImage: response.data.profileImage }));
      setSuccess('Profile image updated!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to upload image: ' + (err.response?.data?.message || err.message));
      setTimeout(() => setError(''), 3000);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleRemoveImage = async () => {
    try {
      await adminApi.removeTutorProfileImage(id);
      setTutor(prev => ({ ...prev, profileImage: null }));
      setSuccess('Profile image removed!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to remove image: ' + (err.response?.data?.message || err.message));
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleNameSave = async () => {
    const trimmed = editedName.trim();
    if (!trimmed || trimmed === tutor.name) {
      setEditingName(false);
      return;
    }
    try {
      const response = await adminApi.updateTutor(id, { name: trimmed });
      setTutor(response.data);
      setFormData(response.data);
      setSuccess('Name updated successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to update name: ' + (err.response?.data?.message || err.message));
      setTimeout(() => setError(''), 3000);
    }
    setEditingName(false);
  };

  const handleResetPassword = async () => {
    if (window.confirm(`Reset password for ${tutor.name}? Password will be reset to the default and they will be required to change it on next login.`)) {
      try {
        await adminApi.resetTutorPassword(id);
        setSuccess('Password reset successfully! Default password set (tutor must change on next login)');
        setTimeout(() => setSuccess(''), 5000);
      } catch (err) {
        setError('Failed to reset password: ' + (err.response?.data?.message || err.message));
        setTimeout(() => setError(''), 3000);
      }
    }
  };

  const handleArchive = async () => {
    if (window.confirm(`Archive ${tutor.name}? They will be marked as inactive but data will be preserved.`)) {
      try {
        await adminApi.deleteTutor(id);
        setSuccess('Tutor archived successfully!');
        setTimeout(() => setSuccess(''), 3000);
        // Refresh the tutor data to show updated status
        await fetchTutorDetails();
      } catch (err) {
        setError('Failed to archive tutor: ' + (err.response?.data?.message || err.message));
        setTimeout(() => setError(''), 3000);
      }
    }
  };

  const handleUnarchive = async () => {
    if (window.confirm(`Reactivate ${tutor.name}?`)) {
      try {
        // Use bulk unarchive API for single tutor
        await adminApi.bulkUnarchiveTutors([id]);
        setSuccess('Tutor reactivated successfully!');
        setTimeout(() => setSuccess(''), 3000);
        // Refresh the tutor data to show updated status
        await fetchTutorDetails();
      } catch (err) {
        setError('Failed to reactivate tutor: ' + (err.response?.data?.message || err.message));
        setTimeout(() => setError(''), 3000);
      }
    }
  };

  const handlePermanentDelete = async () => {
    if (window.confirm(`⚠️ PERMANENT DELETE: Are you sure you want to permanently delete ${tutor.name}? This action CANNOT be undone and will remove all data including lessons, subjects, and history.`)) {
      if (window.confirm(`Final confirmation: Type the tutor's name to confirm deletion.\n\nThis will permanently delete: ${tutor.name}`)) {
        try {
          await adminApi.permanentlyDeleteTutor(id);
          setSuccess('Tutor permanently deleted.');
          setTimeout(() => {
            navigate('/admin/tutors');
          }, 1500);
        } catch (err) {
          setError('Failed to delete tutor: ' + (err.response?.data?.message || err.message));
          setTimeout(() => setError(''), 3000);
        }
      }
    }
  };

  if (loading) return <div className="loading">Loading tutor details...</div>;
  if (error && !tutor) return <div className="error">{error}</div>;
  if (!tutor) return <div className="error">Tutor not found</div>;

  return (
    <div className="tutor-profile">
      {success && <div className="alert alert-success">{success}</div>}
      {error && <div className="alert alert-error">{error}</div>}

      <button className="btn-back-modern" onClick={() => navigate('/admin/tutors')}>
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M12 4L6 10l6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        Back to Tutors
      </button>

      <div className="profile-header">
        <div className="profile-header-main">
          <div className="profile-avatar profile-avatar-upload" onClick={() => document.getElementById('admin-avatar-upload').click()}>
            {tutor.profileImage ? (
              <img src={tutor.profileImage} alt="Profile" className="profile-avatar-img" />
            ) : (
              tutor.name.charAt(0).toUpperCase()
            )}
            <div className="avatar-upload-overlay">
              {uploadingImage ? '...' : (
                <>
                  {tutor.profileImage && (
                    <button className="avatar-overlay-btn" title="View photo" onClick={(e) => { e.stopPropagation(); window.open(tutor.profileImage, '_blank'); }}>
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <circle cx="8" cy="8" r="3" stroke="#fff" strokeWidth="1.5"/>
                        <path d="M1 8s2.5-5 7-5 7 5 7 5-2.5 5-7 5-7-5-7-5z" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                  )}
                  <button className="avatar-overlay-btn" title="Upload photo" onClick={(e) => { e.stopPropagation(); document.getElementById('admin-avatar-upload').click(); }}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M8 2v8M4 6l4-4 4 4M2 12h12" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                  {tutor.profileImage && (
                    <button className="avatar-overlay-btn remove" title="Remove photo" onClick={async (e) => {
                      e.stopPropagation();
                      await handleRemoveImage();
                    }}>
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M3 3l10 10M13 3L3 13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                      </svg>
                    </button>
                  )}
                </>
              )}
            </div>
            <input id="admin-avatar-upload" type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImageUpload} />
          </div>
          <div className="profile-header-info">
            {editingName ? (
              <input
                type="text"
                value={editedName}
                onChange={e => setEditedName(e.target.value)}
                onBlur={handleNameSave}
                onKeyDown={e => { if (e.key === 'Enter') handleNameSave(); if (e.key === 'Escape') setEditingName(false); }}
                autoFocus
                style={{ fontSize: 'inherit', fontWeight: 'inherit', fontFamily: 'inherit', border: 'none', borderBottom: '2px solid #4f46e5', outline: 'none', background: 'transparent', padding: '0', width: '100%', maxWidth: '320px' }}
              />
            ) : (
              <h1
                onClick={() => { setEditedName(tutor.name); setEditingName(true); }}
                title="Click to edit name"
                style={{ cursor: 'pointer' }}
              >
                {tutor.name}
              </h1>
            )}
            <p className="profile-email">{tutor.email}</p>
            <div className="profile-badges">
              <span className={`status-badge-new ${tutor.isActive ? 'active' : 'inactive'}`}>
                <span className="badge-dot"></span>
                {tutor.isActive ? 'Active' : 'Inactive'}
              </span>
              <span className="role-badge-new">Tutor</span>
              {tutor.code && (
                <span className="code-badge-new">{tutor.code}</span>
              )}
            </div>
            {(tutor.createdAt || tutor.updatedAt) && (
              <div className="profile-timestamps-footer">
                {tutor.createdAt && (
                  <span>Created {new Date(tutor.createdAt).toLocaleString('en-SG', { 
                    timeZone: 'Asia/Singapore',
                    month: 'short', 
                    day: 'numeric', 
                    year: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true
                  })}</span>
                )}
                {tutor.createdAt && tutor.updatedAt && <span className="timestamp-separator">•</span>}
                {tutor.updatedAt && (
                  <span>Last modified {new Date(tutor.updatedAt).toLocaleString('en-SG', { 
                    timeZone: 'Asia/Singapore',
                    month: 'short', 
                    day: 'numeric', 
                    year: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true
                  })}</span>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="profile-actions-redesigned">
          <button 
            className="action-button reset-password-style"
            onClick={handleResetPassword}
            title="Reset Password"
            type="button"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M4 7V5a4 4 0 0 1 8 0v2M3 7h10l1 8H2L3 7z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Reset Password
          </button>
          {tutor.isActive ? (
            <button 
              className="action-button archive-style"
              onClick={handleArchive}
              title="Archive Tutor"
              type="button"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M2 3h12M2 3v10a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V3M5 3V1a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M6 8h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Archive
            </button>
          ) : (
            <>
              <button 
                className="action-button reactivate-style"
                onClick={handleUnarchive}
                title="Reactivate Tutor"
                type="button"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M2 3h12M2 3v10a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V3M5 3V1a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M6 8l2 2 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Reactivate
              </button>
              <button 
                className="action-button delete-style"
                onClick={handlePermanentDelete}
                title="Permanently Delete Tutor"
                type="button"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M2 4h12M5.5 4V3a1.5 1.5 0 0 1 1.5-1.5h2A1.5 1.5 0 0 1 10.5 3v1m3 0v9a2 2 0 0 1-2 2h-7a2 2 0 0 1-2-2V4h12zM6.5 7.5v4M9.5 7.5v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Delete
              </button>
            </>
          )}
        </div>
      </div>

      <div className="profile-content">
        <div className={`profile-section editable-section ${editingSection === 'personal' ? 'editing' : ''}`}>
          <button 
            className="btn-edit-corner" 
            onClick={() => editingSection === 'personal' ? cancelEditing() : startEditing('personal')}
            title={editingSection === 'personal' ? "Cancel" : "Edit"}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              {editingSection === 'personal' ? (
                <path d="M3 3l8 8M11 3l-8 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              ) : (
                <path d="M10 1.5a1.5 1.5 0 0 1 2.1 2.1L4.5 11.1l-3 .4.4-3L10 1.5z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              )}
            </svg>
          </button>
          
          <h2>Personal Information</h2>
          {editingSection === 'personal' ? (
            <>
              <div className="info-grid">
                <div className={`info-item ${fieldErrors.email ? 'has-error' : ''}`}>
                  <label>Email</label>
                  <input 
                    type="email" 
                    name="email" 
                    value={formData.email || ''} 
                    onChange={handleInputChange}
                    placeholder="e.g., tutor@example.com"
                  />
                  {fieldErrors.email && (
                    <>
                      <span className="field-error-icon">⚠</span>
                      <div className="field-error-tooltip">{fieldErrors.email}</div>
                    </>
                  )}
                </div>
                <div className={`info-item ${fieldErrors.dateOfBirth ? 'has-error' : ''}`}>
                  <label>Date of Birth{formData.dateOfBirth ? ` (${calculateAge(formData.dateOfBirth)} years)` : ''}</label>
                  <input 
                    type="date" 
                    name="dateOfBirth" 
                    value={formData.dateOfBirth ? new Date(formData.dateOfBirth).toLocaleDateString('en-CA', { timeZone: 'Asia/Singapore' }) : ''} 
                    onChange={handleInputChange}
                  />
                  {fieldErrors.dateOfBirth && (
                    <>
                      <span className="field-error-icon">⚠</span>
                      <div className="field-error-tooltip">{fieldErrors.dateOfBirth}</div>
                    </>
                  )}
                </div>
                <div className="info-item">
                  <label>Gender</label>
                  <select name="gender" value={formData.gender || ''} onChange={handleInputChange}>
                    <option value="">Select gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div className="info-item">
                  <label>Race</label>
                  <input 
                    type="text" 
                    name="race" 
                    value={formData.race || ''} 
                    onChange={handleInputChange}
                    placeholder="e.g., Chinese, Malay, Indian"
                  />
                </div>
                <div className={`info-item ${fieldErrors.phone ? 'has-error' : ''}`}>
                  <label>Phone</label>
                  <input 
                    type="tel" 
                    name="contact.phone" 
                    value={formData.contact?.phone || formData.phone || ''} 
                    onChange={handleInputChange}
                    placeholder="e.g., +65 9123 4567"
                  />
                  {fieldErrors.phone && (
                    <>
                      <span className="field-error-icon">⚠</span>
                      <div className="field-error-tooltip">{fieldErrors.phone}</div>
                    </>
                  )}
                </div>
                <div className="info-item">
                  <label>Country</label>
                  <input 
                    type="text" 
                    name="country" 
                    value={formData.country || ''} 
                    onChange={handleInputChange}
                    placeholder="e.g., Singapore"
                  />
                </div>
                <div className="info-item">
                  <label>Address</label>
                  <input type="text" name="contact.address" value={formData.contact?.address || formData.address || ''} onChange={handleInputChange} />
                </div>
                <div className="info-item">
                  <label>LinkedIn</label>
                  <input type="url" name="linkedin" value={formData.linkedin || ''} onChange={handleInputChange} />
                </div>
                <div className="info-item full-width">
                  <label>Bio</label>
                  <textarea name="bio" value={formData.bio || ''} onChange={handleInputChange} rows="4" />
                </div>
              </div>
              <div className="section-actions">
                <button className="btn-save-section" onClick={() => saveSection('Personal Information')}>Save</button>
              </div>
            </>
          ) : (
            <div className="info-grid">
              <div className="info-item">
                <label>Email</label>
                <p>{tutor.email}</p>
              </div>
              <div className="info-item">
                <label>Date of Birth</label>
                <p>
                  {tutor.dateOfBirth 
                    ? `${new Date(tutor.dateOfBirth).toLocaleDateString('en-SG', { timeZone: 'Asia/Singapore' }) } (${calculateAge(tutor.dateOfBirth)} years)`
                    : 'N/A'
                  }
                </p>
              </div>
              <div className="info-item">
                <label>Gender</label>
                <p>{tutor.gender || 'N/A'}</p>
              </div>
              <div className="info-item">
                <label>Race</label>
                <p>{tutor.race || 'N/A'}</p>
              </div>
              <div className="info-item">
                <label>Phone</label>
                <p>{tutor.contact?.phone || tutor.phone || 'N/A'}</p>
              </div>
              <div className="info-item">
                <label>Country</label>
                <p>{tutor.country || 'Singapore'}</p>
              </div>
              <div className="info-item">
                <label>Address</label>
                <p>{tutor.contact?.address || tutor.address || 'N/A'}</p>
              </div>
              <div className="info-item">
                <label>LinkedIn</label>
                <p>
                  {tutor.linkedin ? (
                    <a href={tutor.linkedin} target="_blank" rel="noopener noreferrer" className="linkedin-link">
                      {tutor.linkedin}
                    </a>
                  ) : 'N/A'}
                </p>
              </div>
              <div className="info-item full-width">
                <label>Bio</label>
                <p className="bio-text">{tutor.bio || 'N/A'}</p>
              </div>
            </div>
          )}
        </div>

        <div className={`profile-section editable-section ${editingSection === 'education' ? 'editing' : ''}`}>
          <button 
            className="btn-edit-corner" 
            onClick={() => editingSection === 'education' ? cancelEditing() : startEditing('education')}
            title={editingSection === 'education' ? "Cancel" : "Edit"}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              {editingSection === 'education' ? (
                <path d="M3 3l8 8M11 3l-8 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              ) : (
                <path d="M10 1.5a1.5 1.5 0 0 1 2.1 2.1L4.5 11.1l-3 .4.4-3L10 1.5z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              )}
            </svg>
          </button>
          
          <h2>Education History</h2>
          
          {editingSection === 'education' ? (
            <>
              <div className="education-section">
                {educationList.length === 0 ? (
                  <p className="no-data">No education added. Click "Add Education" to start.</p>
                ) : (
                  educationList.map((edu, index) => (
                    <div key={index} className="education-entry editable">
                      <button className="btn-remove-education" onClick={() => removeEducation(index)} title="Remove">
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                          <path d="M3 3l8 8M11 3l-8 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                      </button>
                      <div className="education-entry-header">
                        <h3>Education {index + 1}</h3>
                      </div>
                      <div className="education-form">
                        <div className="form-row">
                          <div className="form-group">
                            <label>Level</label>
                            <select value={edu.level || ''} onChange={(e) => updateEducation(index, 'level', e.target.value)}>
                              <option value="">Select level</option>
                              <option value="GCE 'O' Level">GCE 'O' Level</option>
                              <option value="GCE 'A' Level">GCE 'A' Level</option>
                              <option value="IB Diploma">IB Diploma</option>
                              <option value="Diploma">Diploma</option>
                              <option value="Bachelor's Degree">Bachelor's Degree</option>
                              <option value="Master's Degree">Master's Degree</option>
                              <option value="PhD">PhD</option>
                              <option value="Other">Other</option>
                            </select>
                          </div>
                          <div className="form-group">
                            <label>Institution</label>
                            <input type="text" value={edu.institution || ''} onChange={(e) => updateEducation(index, 'institution', e.target.value)} placeholder="School/University name" />
                          </div>
                        </div>
                        <div className="form-row">
                          <div className="form-group">
                            <label>Major/Field</label>
                            <input type="text" value={edu.major || ''} onChange={(e) => updateEducation(index, 'major', e.target.value)} placeholder="e.g., Mathematics" />
                          </div>
                          <div className={`form-group ${fieldErrors[`yearGraduated${index}`] ? 'has-error' : ''}`} style={{position: 'relative'}}>
                            <label>Year Graduated</label>
                            <input 
                              type="number" 
                              value={edu.yearGraduated || ''} 
                              onChange={(e) => updateEducation(index, 'yearGraduated', e.target.value)} 
                              placeholder="e.g., 2020"
                            />
                            {fieldErrors[`yearGraduated${index}`] && (
                              <>
                                <span className="field-error-icon">⚠</span>
                                <div className="field-error-tooltip">{fieldErrors[`yearGraduated${index}`]}</div>
                              </>
                            )}
                          </div>
                        </div>
                        <div className="form-group">
                          <label>Subjects</label>
                          <input type="text" value={edu.subjects || ''} onChange={(e) => updateEducation(index, 'subjects', e.target.value)} placeholder="e.g., Mathematics, Physics" />
                        </div>
                        <div className="form-group">
                          <label>Grades</label>
                          <input type="text" value={edu.grades || ''} onChange={(e) => updateEducation(index, 'grades', e.target.value)} placeholder="e.g., A, B+" />
                        </div>
                      </div>
                    </div>
                  ))
                )}
                <button className="btn-add-education" onClick={addEducation}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                  Add Education
                </button>
              </div>
              <div className="section-actions">
                <button className="btn-save-section" onClick={() => saveSection('Education History')}>Save</button>
              </div>
            </>
          ) : (
            <div className="education-list">
              {tutor.educationHistory && tutor.educationHistory.length > 0 ? (
                [...tutor.educationHistory]
                  .sort((a, b) => (b.yearGraduated || 0) - (a.yearGraduated || 0))
                  .map((edu, index) => (
                  <div key={index} className="education-card">
                    <div className="education-header">
                      <h3>{edu.level || 'Education'}</h3>
                      {edu.yearGraduated && <span className="year">{edu.yearGraduated}</span>}
                    </div>
                    <p className="institution">{edu.institution || 'N/A'}</p>
                    {edu.major && <p className="major">Major: {edu.major}</p>}
                    {edu.subjects && <p className="subjects">Subjects: {edu.subjects}</p>}
                    {edu.grades && <p className="grades">Grades: {edu.grades}</p>}
                  </div>
                ))
              ) : (
                <p className="no-data">No education information available</p>
              )}
            </div>
          )}
        </div>

        <div className="profile-section">
          <h2>Assigned Tutees ({tutees.length})</h2>
          {tutees.length > 0 ? (
            <div className="education-list">
              {tutees.map(tutee => (
                <div key={tutee._id} className="education-card">
                  <div className="education-header">
                    <h3>{tutee.name}</h3>
                    <span className={`status-badge ${tutee.isActive !== false ? 'active' : 'inactive'}`}>
                      {tutee.isActive !== false ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <p className="institution">{tutee.school || 'N/A'}</p>
                  <p className="major">Level: {tutee.level || 'N/A'}</p>
                  {tutee.subjects && tutee.subjects.length > 0 && (
                    <p className="subjects">
                      Subjects: {tutee.subjects.map(s => s.name).join(', ')}
                    </p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="no-data">No tutees assigned yet</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default TutorDetail;